import { useMemo, useCallback } from 'react';
import { useData } from './useData';
import { useApp } from '../contexts/AppContext';

/**
 * useNotifications - Hook for managing in-app notifications
 *
 * Provides:
 * - Notification data fetching
 * - Mark as read/unread
 * - Dismiss notifications
 * - Unread count
 */
export const useNotifications = () => {
  const { currentProject } = useApp();

  // Fetch notifications for current user
  const {
    data: notifications,
    isLoading,
    update: updateNotification,
  } = useData('notifications', {
    enabled: true,
    realtime: true,
    orderBy: { column: 'created_at', ascending: false },
  });

  // Filter to current project if one is selected
  const projectNotifications = useMemo(() => {
    if (!notifications) return [];
    if (!currentProject?.id) return notifications;
    return notifications.filter(n => !n.project_id || n.project_id === currentProject.id);
  }, [notifications, currentProject?.id]);

  // Get unread count
  const unreadCount = useMemo(() => {
    return projectNotifications.filter(n => !n.read && !n.dismissed).length;
  }, [projectNotifications]);

  // Get recent notifications (last 10, not dismissed)
  const recentNotifications = useMemo(() => {
    return projectNotifications.filter(n => !n.dismissed).slice(0, 10);
  }, [projectNotifications]);

  // Mark notification as read
  const markAsRead = useCallback(
    async notificationId => {
      try {
        await updateNotification({
          id: notificationId,
          data: { read: true },
        });
      } catch (error) {
        console.error('Error marking notification as read:', error);
      }
    },
    [updateNotification],
  );

  // Mark all as read
  const markAllAsRead = useCallback(async () => {
    try {
      const unreadIds = projectNotifications.filter(n => !n.read).map(n => n.id);

      for (const id of unreadIds) {
        await updateNotification({
          id,
          data: { read: true },
        });
      }
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    }
  }, [projectNotifications, updateNotification]);

  // Dismiss notification
  const dismissNotification = useCallback(
    async notificationId => {
      try {
        await updateNotification({
          id: notificationId,
          data: { dismissed: true },
        });
      } catch (error) {
        console.error('Error dismissing notification:', error);
      }
    },
    [updateNotification],
  );

  // Get notification type icon/color
  const getNotificationStyle = useCallback(type => {
    const styles = {
      quote_expiring: { color: '#f59e0b', label: 'הצעה פגה' },
      payment_due: { color: '#ef4444', label: 'תשלום' },
      mention: { color: '#2563eb', label: 'אזכור' },
      schedule_change: { color: '#8b5cf6', label: 'לוח זמנים' },
      other: { color: '#64748b', label: 'הודעה' },
    };
    return styles[type] || styles.other;
  }, []);

  return {
    notifications: projectNotifications,
    recentNotifications,
    unreadCount,
    isLoading,
    markAsRead,
    markAllAsRead,
    dismissNotification,
    getNotificationStyle,
  };
};

export default useNotifications;
